import {call, debounce, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {logError} from '@yandex-int/pythia-libs/lib/rum';

import {SurveyState} from 'client/common/store/reducers/survey/types';
import {
    ListFilter,
    ListMode,
    StateFilterValue,
    SurveyItem
} from 'client/common/store/reducers/surveys/types';

import {
    changeClientIdFilter,
    changeNameFilter,
    changeOrgFilter,
    changeStateFilter,
    clearSurveyList,
    fetchSurveyList,
    pushToSurveyList,
    resetFilter,
    savePaginationKey,
    setSurveyListIsEntirelyLoaded
} from 'client/common/store/reducers/surveys';

import {
    getListMode,
    getPaginationKey,
    getSurveyFilter
} from 'client/common/store/selectors/surveys';

import {
    ErrorResponse,
    GetSurveysResponse,
    SurveysFetchFormat,
    SurveysFetchParams
} from 'client/common/utils/api-client/types';

import {getSurveys} from 'client/common/utils/api-client/constructor';
import cfg from 'client/common/utils/client-config';
import {FILTER_KEY, sessionStorageClient} from 'client/common/utils/storage';
import {SurveyType} from 'client/common/types';

import {ApiResponse} from 'client/common/store/saga/types';

const FORMATS = {
    [ListMode.Agency]: SurveysFetchFormat.Agency,
    [ListMode.AgencyClient]: SurveysFetchFormat.Quicksurvey,
    [ListMode.User]: SurveysFetchFormat.Quicksurvey
};

export function genSurveysFetchParams(params: {
    paginationKey: string | undefined;
    filter: ListFilter;
    listMode: ListMode;
    isCreativeAssessor?: boolean;
    format?: SurveysFetchFormat;
}): SurveysFetchParams {
    const {paginationKey, filter, listMode, format, isCreativeAssessor} = params;

    const minCount = cfg.surveyListMinCount;

    const fetchFormat = format || FORMATS[listMode];

    const {clientId, name, orgIds} = filter;
    const state =
        filter.state === StateFilterValue.All
            ? Object.values(SurveyState).filter(surveyState => surveyState !== SurveyState.Closed)
            : (filter.state as unknown as SurveyState);

    // не показываем в общем списке брендлифты и тесты креативов для обычных пользователей
    const surveyType: SurveyType[] = filter?.surveyType ?? [
        'videoBox',
        'videoBoxKZ',
        'createSegment',
        'PRO',
        'ux'
    ];

    if (isCreativeAssessor) {
        surveyType.push('creative', 'bordomatik');
    }

    return {
        clientId,
        format: fetchFormat,
        minCount,
        name,
        paginationKey,
        state,
        surveyType,
        orgIds
    };
}

export function* handleFetchSurveyList({payload}: ReturnType<typeof fetchSurveyList>) {
    const {isCreativeAssessor} = payload;

    const paginationKey: string | undefined = yield select(getPaginationKey);
    const filter: ListFilter = yield select(getSurveyFilter);

    const listMode: ListMode = yield select(getListMode);

    const fetchParams = genSurveysFetchParams({
        paginationKey,
        filter,
        listMode,
        isCreativeAssessor
    });

    const res: ApiResponse<typeof getSurveys> = yield call(getSurveys, fetchParams);

    if (res.status === 200 && res.data) {
        // @ts-ignore
        yield handleFetchSurveyListSuccess(res.data);
    } else {
        // @ts-ignore
        yield handleFetchSurveyListFailure(res);
    }
}

export function* handleFetchSurveyListSuccess(data: GetSurveysResponse['data']) {
    const {paginationKey, surveys} = data;

    if (surveys.length < cfg.surveyListMinCount) {
        yield put(setSurveyListIsEntirelyLoaded());
    }

    const items: SurveyItem[] = surveys.map(
        ({
            slug,
            name,
            state,
            audience,
            clientId,
            clientName,
            cost,
            surveyType,
            surveyScope,
            updatedAt,
            paymentStatus,
            moderationVerdict
        }) => ({
            slug,
            name,
            state,
            audience,
            clientId: clientId || undefined,
            clientName,
            cost,
            surveyType,
            surveyScope,
            updatedAt,
            isLoading: false,
            isTemporary: false,
            paymentStatus,
            moderationVerdict
        })
    );

    yield put(savePaginationKey({paginationKey}));
    yield put(pushToSurveyList({items}));
}

export function* handleFetchSurveyListFailure(res: ErrorResponse) {
    yield call(logError, {message: 'Can not fetch survey', additional: {data: res.data}});
}

function* handleSurveyFilterChange() {
    const filter: ListFilter = yield select(getSurveyFilter);

    yield call(sessionStorageClient.saveDataToStorage, FILTER_KEY, filter);

    yield put(clearSurveyList());
}

export default function* router() {
    yield takeLatest(fetchSurveyList, handleFetchSurveyList);

    yield takeEvery(
        [resetFilter, changeClientIdFilter, changeStateFilter, changeOrgFilter],
        handleSurveyFilterChange
    );
    yield debounce(700, changeNameFilter, handleSurveyFilterChange);
}
